/**
 * /university/ab-tests — A/B experiments list.
 *
 * Active (proposed + running) experiments float to the top; concluded
 * (evaluated / promoted / rejected / aborted) sink below. Each row is a
 * link to the detail page where founders can see significance + decide.
 */
import type { AbTest } from '@chaos/types/learning';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, FlaskConical, Loader2, Plus } from 'lucide-react';
import * as React from 'react';

import { Button } from '../../../../components/ui/button';
import { fmtRelative } from '../../../../components/university/formatters';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth-store';

const STATUS_LABEL: Record<AbTest['status'], string> = {
  proposed: 'Proposed',
  running: 'Running',
  evaluated: 'Evaluated',
  promoted: 'Promoted',
  rejected: 'Rejected',
  aborted: 'Aborted',
};

const STATUS_TONE: Record<AbTest['status'], 'muted' | 'warning' | 'success' | 'error'> = {
  proposed: 'muted',
  running: 'warning',
  evaluated: 'muted',
  promoted: 'success',
  rejected: 'error',
  aborted: 'error',
};

const TONE_DOT: Record<'muted' | 'warning' | 'success' | 'error', string> = {
  muted: 'bg-muted-foreground/40',
  warning: 'bg-warning',
  success: 'bg-success',
  error: 'bg-destructive',
};

const ACTIVE: ReadonlySet<AbTest['status']> = new Set(['proposed', 'running']);

function AbTestsIndexRoute() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canCreate = role === 'founder' || role === 'admin' || role === 'tuner';

  const listQ = useQuery({
    queryKey: learningKeys.abTestsList(),
    queryFn: () => learningApi.listAbTests(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const { active, concluded } = React.useMemo(() => {
    const tests = listQ.data?.tests ?? [];
    const a: AbTest[] = [];
    const c: AbTest[] = [];
    for (const t of tests) {
      (ACTIVE.has(t.status) ? a : c).push(t);
    }
    a.sort((x, y) => Date.parse(y.startedAt) - Date.parse(x.startedAt));
    c.sort((x, y) => Date.parse(y.endsAt) - Date.parse(x.endsAt));
    return { active: a, concluded: c };
  }, [listQ.data]);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <Link
          to="/university"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to University
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-foreground" aria-hidden />
            <h1 className="text-[16px] font-semibold tracking-tight text-foreground">A/B tests</h1>
            <span className="text-[12px] font-normal text-muted-foreground">
              {active.length} active · {concluded.length} concluded
            </span>
          </div>
          <div className="flex items-center gap-2">
            {listQ.isFetching ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
              </span>
            ) : null}
            {canCreate ? (
              <Button asChild size="sm">
                <Link to="/university/ab-tests/new">
                  <Plus className="h-3.5 w-3.5" aria-hidden /> New test
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
        <p className="text-[12px] text-muted-foreground">
          Compare a candidate prompt against the active version. Welch's t-test on composite score;
          founder approval required to promote.
        </p>
      </header>

      {listQ.isPending ? (
        <div className="px-2 py-4 text-[12px] text-muted-foreground">Loading A/B tests…</div>
      ) : listQ.isError ? (
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Failed to load A/B tests: {(listQ.error as Error).message}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Group title="Active" tests={active} emptyHint="No experiments running right now." />
          <Group
            title="Concluded"
            tests={concluded}
            emptyHint="History will show here once experiments wrap up."
          />
        </div>
      )}
    </section>
  );
}

function Group({ title, tests, emptyHint }: { title: string; tests: AbTest[]; emptyHint: string }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="px-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {tests.length === 0 ? (
        <div className="rounded border border-dashed border-border bg-muted/10 p-4 text-center text-[12px] text-muted-foreground">
          {emptyHint}
        </div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {tests.map((t) => (
            <AbTestRow key={t.id} test={t} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AbTestRow({ test }: { test: AbTest }) {
  const tone = STATUS_TONE[test.status];
  return (
    <li className="group/row relative flex flex-col gap-1 rounded border border-border bg-background p-3 transition-colors hover:bg-muted/40">
      <Link
        to="/university/ab-tests/$testId"
        params={{ testId: test.id }}
        className="absolute inset-0 z-0 rounded"
        aria-label={`Open A/B test ${test.id}`}
      />
      <div className="relative z-10 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT[tone])} />
            <span className="truncate text-[13px] font-semibold text-foreground">
              {test.skillName ?? `Skill ${test.skillId.slice(0, 8)}`}
            </span>
            <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              {STATUS_LABEL[test.status]}
            </span>
          </div>
          <span className="font-mono text-[11px] text-muted-foreground">
            {test.controlVersion} → {test.candidateVersion} · split{' '}
            {(test.trafficSplit * 100).toFixed(0)}% candidate
          </span>
        </div>
        <span className="text-[11px] text-muted-foreground">
          {ACTIVE.has(test.status) ? (
            <>started {fmtRelative(test.startedAt)}</>
          ) : (
            <>ended {fmtRelative(test.endedAt ?? test.endsAt)}</>
          )}
        </span>
      </div>
    </li>
  );
}

export const Route = createFileRoute('/_authenticated/university/ab-tests/')({
  component: AbTestsIndexRoute,
});
